import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { BookOpen, CheckCircle2, Clock, Flame } from "lucide-react";

const CATEGORY_LABEL = {
  algorithm: "Algorithm",
  growth: "Growth",
  money: "Money",
  craft: "Craft",
};

const CATEGORY_COLOR = {
  algorithm: "text-cyan-300 border-cyan-300/30 bg-cyan-300/10",
  growth: "text-[#CCFF00] border-[#CCFF00]/30 bg-[#CCFF00]/10",
  money: "text-amber-300 border-amber-300/30 bg-amber-300/10",
  craft: "text-fuchsia-300 border-fuchsia-300/30 bg-fuchsia-300/10",
};

const DIFFICULTY_COLOR = {
  beginner: "text-neutral-400",
  intermediate: "text-yellow-400",
  advanced: "text-red-400",
};

export default function Playbooks() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], stats: { total: 0, read: 0 } });
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const endpoint = user ? "/playbooks" : "/playbooks/public";
    api.get(endpoint).then((r) => setData(r.data)).catch(() => {});
  }, [user]);

  const items = filter === "all" ? data.items : data.items.filter((p) => p.category === filter);
  const readPct = data.stats.total ? Math.round(((data.stats.read || 0) / data.stats.total) * 100) : 0;
  const linkBase = user ? "/app/playbooks" : "/playbooks";

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <SEO
        title="The Playbook Library"
        description="15 hand-crafted insider playbooks for creators — algorithm mechanics, growth compounding, and brand-deal money moves. Free to browse."
        path="/playbooks"
      />
      <div className="mb-12">
        <p className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] mb-2">
          // the playbook library
        </p>
        <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter">
          Insider knowledge. <br />
          No fluff.
        </h1>
        <p className="mt-4 text-neutral-400 max-w-2xl">
          The hard-won secrets top creators pay $500 courses to learn — bundled into 2-minute reads,
          each with one action to take today.
        </p>
      </div>

      {/* Progress + filters */}
      <div className="grid md:grid-cols-4 gap-4 mb-8" data-testid="playbooks-stats">
        <div className="p-5 rounded-2xl bg-[#CCFF00] text-black">
          <p className="text-[10px] font-mono uppercase tracking-widest text-black/60">
            {user ? "Progress" : "Playbooks"}
          </p>
          <p className="font-display font-black text-4xl mt-1 leading-none">
            {user ? `${data.stats.read || 0}/${data.stats.total}` : data.stats.total}
          </p>
          {user && (
            <div className="mt-3 h-1.5 bg-black/20 rounded-full overflow-hidden">
              <div className="h-full bg-black" style={{ width: `${readPct}%` }} />
            </div>
          )}
        </div>
        {["algorithm", "growth", "money"].map((c) => (
          <button
            key={c}
            onClick={() => setFilter(filter === c ? "all" : c)}
            className={`p-5 rounded-2xl border transition-colors text-left ${
              filter === c
                ? "bg-white/10 border-white/30"
                : "bg-[#141414] border-white/5 hover:border-white/15"
            }`}
            data-testid={`playbooks-filter-${c}`}
          >
            <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
              {CATEGORY_LABEL[c]}
            </p>
            <p className="font-display font-black text-4xl mt-1 leading-none">
              {data.stats.by_category?.[c] || 0}
            </p>
          </button>
        ))}
      </div>

      {filter !== "all" && (
        <button
          onClick={() => setFilter("all")}
          className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-white mb-6"
          data-testid="playbooks-clear-filter"
        >
          ← show all
        </button>
      )}

      {/* Grid */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="playbooks-grid">
        {items.map((p) => (
          <Link to={`${linkBase}/${p.slug}`} key={p.slug} data-testid={`playbook-card-${p.slug}`}>
            <div
              className={`p-6 rounded-2xl border transition-all h-full ${
                p.read
                  ? "bg-[#141414] border-[#CCFF00]/30 hover:-translate-y-1"
                  : "bg-[#141414] border-white/5 hover:border-white/25 hover:-translate-y-1"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <span
                  className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded border ${
                    CATEGORY_COLOR[p.category]
                  }`}
                >
                  {CATEGORY_LABEL[p.category]}
                </span>
                {p.read && <CheckCircle2 className="w-4 h-4 text-[#CCFF00]" />}
              </div>
              <h3 className="font-display font-black text-2xl mb-2 tracking-tight leading-tight">
                {p.title}
              </h3>
              <p className="text-sm text-neutral-400 mb-4 leading-relaxed">{p.one_liner}</p>
              <div className="flex items-center gap-4 text-xs font-mono text-neutral-500">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {p.read_time_min} min
                </span>
                <span className={DIFFICULTY_COLOR[p.difficulty]}>{p.difficulty}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
